import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import React, { useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { countryCodes, defaultCountryCodes, ICountryCode } from 'src/services/countryCodes';
import TextInput, { createInputId } from '../TextInput/TextInput';
import globalMessages from 'src/services/globalMessages';
import Select from '../Select/Select';
import { personalDetailsFields } from 'src/services/validations/userDetailsRules';

interface IPhoneInputProps {
    className?: string;
    defaultValue?: string;
    onChange?: (value: string | null) => void;
    required?: boolean;
    adminCountryCode?: string;
}

const messages = defineMessages({
  countryCode: {
      id: 'countryCode',
      defaultMessage: 'Országelőhívószám',
  },
});

const PhoneInput: React.FC<IPhoneInputProps> = ({
    className: classNameProp,
    defaultValue,
    onChange,
    required,
    adminCountryCode,
}) => {
    const intl = useIntl();
    const defaultCountryCode = !!adminCountryCode
        ? countryCodes.find((s) => s.value === adminCountryCode)
        : defaultCountryCodes[intl.locale as keyof typeof defaultCountryCodes];
    const className = 'phone-input ' + (classNameProp || '');
    const selectId = createInputId('CountryCode');
    const inputId = createInputId(personalDetailsFields.Phone);
    const phoneNumber = defaultValue ? parsePhoneNumberFromString(defaultValue) : undefined;
    const actCountryCode = countryCodes.find((s) => s.value === phoneNumber?.country);
    const initialCountryCode = actCountryCode || defaultCountryCode;
    const [countryCode, setCountryCode] = useState<ICountryCode | undefined>(initialCountryCode);
    const [nationalNumber, setNationalNumber] = useState<string>((phoneNumber?.nationalNumber || '') as string);

    const serizalizePhoneNumber = (newPhoneNumber: string, newCode?: ICountryCode) => {
        const parsedPhoneNumber = parsePhoneNumberFromString(newPhoneNumber, newCode?.value);
        return parsedPhoneNumber
            ? parsedPhoneNumber.format('E.164')
            : nationalNumber
            ? countryCode?.text + nationalNumber
            : '';
    };

    const handleChange = (newPhoneNumber: string, newCode?: ICountryCode) => {
        const phoneNumberString = serizalizePhoneNumber(newPhoneNumber, newCode);
        if (onChange) {
            onChange(phoneNumberString);
        }
    };

    const handleCountryCodeChange = (countryCodeValue: string) => {
        const newCountryCode = countryCodes.find((s) => s.value === countryCodeValue);
        if (newCountryCode) {
            setCountryCode(newCountryCode);
            handleChange(nationalNumber, newCountryCode);
        }
    };

    const handleNationalNumberChange = (value: string) => {
      setNationalNumber(value);
      handleChange(value, countryCode);
    };

    return (
        <div className={className}>
            <label htmlFor={selectId} className="phone-input__countrycode-label">
                <FormattedMessage {...messages.countryCode} />
                {required && ' *'}
            </label>
            <Select
                id={selectId}
                className="phone-input__countrycode"
                values={countryCodes.map((code) => {
                    return {
                        value: code.value,
                        label: code.text,
                    };
                })}
                value={countryCode?.value}
                onChange={handleCountryCodeChange}
            />
            <TextInput
                className="phone-input__number"
                fieldName="phoneNumber"
                value={nationalNumber}
                onChange={handleNationalNumberChange}
                label={globalMessages.phoneNumber}
                labelClassName={'phone-input__label'}
                required={required}
                id={inputId}
            />
        </div>
    );
};

export default PhoneInput;
